import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { palette, Icon } from '@m12s/component-library';
import BaseModal from 'components/BaseModal';
import { ModalButton } from 'components/BaseModal/styled';
import { minus, plus } from 'lib/icons';
import { actionClosePartCountMultiplierDialog } from 'lib/actions';
import { PCM_MAX_VALUE } from 'lib/constants/partCountMultiplier';
import {
  getPartCountMultiplierDialogOpen,
  getPartCountMultiplierDialogDefaultValue,
} from 'lib/selectors/getPartCountMultiplierDialog';
import PartCountMultiplierDialogEventEmitter from './emitter';

const TwoInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const EqualsSign = styled.span`
  font-size: 1.5rem;
  color: ${palette.Grey700};
  flex-shrink: 0;
  line-height: 1;
`;

const UnitInput = styled.div`
  display: flex;
  align-items: stretch;
  border: 1px solid ${palette.Grey400};
  border-radius: 4px;
  background: ${palette.White};
  overflow: hidden;
  height: 2.5rem;
  flex: 1 0 0;
  min-width: 0;
`;

const UnitInputField = styled.input`
  flex: 1 0 0;
  min-width: 0;
  height: 100%;
  padding: 0 0.625rem;
  border: none;
  border-right: 1px solid ${palette.Grey300};
  background: ${palette.White};
  font-size: 0.875rem;
  font-weight: 400;
  color: ${palette.Grey700};
  letter-spacing: 0.07px;
  outline: none;
`;

const StepButton = styled.button`
  width: 2.5rem;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${palette.White};
  border: none;
  border-left: 1px solid ${palette.Grey300};
  color: ${palette.Grey700};
  cursor: pointer;
  flex-shrink: 0;

  &:disabled {
    color: ${palette.Grey400};
    cursor: default;
  }
`;

const MAX_VALUE = PCM_MAX_VALUE;

export const deriveIntegerPair = (floatValue) => {
  if (!floatValue || floatValue <= 0) return { cycles: 1, parts: 1 };
  if (floatValue >= 1) {
    return { cycles: 1, parts: Math.min(Math.round(floatValue), MAX_VALUE) };
  }
  return { cycles: Math.min(Math.round(1 / floatValue), MAX_VALUE), parts: 1 };
};

const UnitField = ({ value, onChange, unit, max }) => {
  const [focused, setFocused] = useState(false);
  const [rawInput, setRawInput] = useState('');
  const inputRef = useRef(null);

  // Select all text after the re-render that switches displayValue to the raw number.
  // useEffect runs post-render, so the DOM reflects the new value before we select.
  useEffect(() => {
    if (focused && inputRef.current) {
      inputRef.current.select();
    }
  }, [focused]);

  const displayValue = focused
    ? rawInput
    : `${value} ${unit}${value === 1 ? '' : 's'}`;

  return (
    <UnitInput>
      {/* type="text" so the unit label ("1 cycle") can be shown inside the field; inputMode triggers numeric keyboard on mobile */}
      <UnitInputField
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={displayValue}
        onFocus={() => {
          setRawInput(String(value));
          setFocused(true);
        }}
        onBlur={(e) => {
          setFocused(false);
          const parsed = parseInt(e.target.value, 10);
          if (!Number.isNaN(parsed) && parsed >= 1) {
            onChange(Math.min(max, parsed));
          }
        }}
        onChange={(e) => {
          setRawInput(e.target.value);
        }}
      />
      <StepButton
        type="button"
        disabled={value <= 1}
        onClick={() => {
          onChange(value - 1);
        }}
      >
        <Icon icon={minus} size="1rem" />
      </StepButton>
      <StepButton
        type="button"
        disabled={value >= max}
        onClick={() => {
          onChange(value + 1);
        }}
      >
        <Icon icon={plus} size="1rem" />
      </StepButton>
    </UnitInput>
  );
};

UnitField.propTypes = {
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  unit: PropTypes.string.isRequired,
  max: PropTypes.number.isRequired,
};

export const PartCountMultiplierDialogBase = ({
  defaultValue,
  onSave,
  onCancel,
}) => {
  const initial = deriveIntegerPair(defaultValue);
  const [cycles, setCycles] = useState(initial.cycles);
  const [parts, setParts] = useState(initial.parts);

  const handleSave = useCallback(() => {
    onSave(parts / cycles);
  }, [onSave, parts, cycles]);

  return (
    <BaseModal
      isOpen
      onClose={onCancel}
      header="Edit part count method"
      details={
        <TwoInputRow>
          <UnitField
            value={parts}
            onChange={setParts}
            unit="part"
            max={MAX_VALUE}
          />
          <EqualsSign>=</EqualsSign>
          <UnitField
            value={cycles}
            onChange={setCycles}
            unit="cycle"
            max={MAX_VALUE}
          />
        </TwoInputRow>
      }
      buttons={[
        <ModalButton
          key="cancel"
          onClick={onCancel}
          customStyles={`
            background: ${palette.White};
            border: 1px solid ${palette.Grey400};
            color: ${palette.Grey700};
            text-shadow: none;
          `}
          hoverStyles={`background: ${palette.Grey100};`}
        >
          Cancel
        </ModalButton>,
        <ModalButton
          key="save"
          onClick={handleSave}
          customStyles={`
            background: ${palette.Indigo600};
            border: none;
            color: ${palette.White};
            text-shadow: none;
          `}
          hoverStyles={`background: ${palette.Indigo700};`}
        >
          Save
        </ModalButton>,
      ]}
    />
  );
};

PartCountMultiplierDialogBase.propTypes = {
  defaultValue: PropTypes.number,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

PartCountMultiplierDialogBase.defaultProps = {
  defaultValue: 1,
};

const PartCountMultiplierDialog = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(getPartCountMultiplierDialogOpen);
  const defaultValue = useSelector(getPartCountMultiplierDialogDefaultValue);

  const handleSave = useCallback(
    (value) => {
      PartCountMultiplierDialogEventEmitter.fireAndForget('success', value);
      dispatch(actionClosePartCountMultiplierDialog());
    },
    [dispatch]
  );

  const handleCancel = useCallback(() => {
    PartCountMultiplierDialogEventEmitter.fireAndForget('cancel');
    dispatch(actionClosePartCountMultiplierDialog());
  }, [dispatch]);

  if (!isOpen) return null;

  return (
    <PartCountMultiplierDialogBase
      defaultValue={defaultValue}
      onSave={handleSave}
      onCancel={handleCancel}
    />
  );
};

export default PartCountMultiplierDialog;
